export function ensure_trailing_slash(url) {
    if (!url) {
        return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
}

export function normalize_properties_response(payload) {
    if (!Array.isArray(payload)) {
        return [];
    }

    return payload
        .map((property) => ({
            id: property?.id ?? "",
            name: property?.name || "Unnamed Property",
            is_active: Boolean(property?.is_active),
            created_at: property?.created_at || "",
            updated_at: property?.updated_at || ""
        }))
        .filter((property) => property.id !== "" && property.name)
        .sort((first_property, second_property) => first_property.name.localeCompare(second_property.name));
}

function safe_parse_maybe_double_encoded_json(value, fallback_value = null) {
    if (value === null || value === undefined || value === "") {
        return fallback_value;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return fallback_value;
    }

    try {
        let parsed_value = JSON.parse(value);

        if (typeof parsed_value === "string") {
            parsed_value = JSON.parse(parsed_value);
        }

        return parsed_value;
    } catch (error) {
        return fallback_value;
    }
}

function normalize_ajax_payload(payload, fallback_text = "") {
    if (payload === null || payload === undefined || payload === "") {
        return fallback_text
            ? safe_parse_maybe_double_encoded_json(fallback_text, null)
            : null;
    }

    if (typeof payload === "string") {
        return safe_parse_maybe_double_encoded_json(payload, payload);
    }

    return payload;
}

function format_api_errors(error_payload) {
    if (!error_payload) {
        return "Something went wrong while processing the request.";
    }

    const normalized_payload = error_payload.errors || error_payload.detail || error_payload.error || error_payload;

    if (typeof normalized_payload === "string") {
        return normalized_payload;
    }

    if (Array.isArray(normalized_payload)) {
        return normalized_payload.join(", ");
    }

    if (typeof normalized_payload === "object") {
        return Object.entries(normalized_payload)
            .map(([field, messages]) => {
                const message_text = Array.isArray(messages) ? messages.join(", ") : messages;
                return `${field}: ${message_text}`;
            })
            .join(" | ");
    }

    return "Something went wrong while processing the request.";
}

function handle_ajax_success(resolve, response_data, jqXHR) {
    resolve(normalize_ajax_payload(response_data, jqXHR?.responseText || ""));
}

function handle_ajax_error(reject, jqXHR) {
    const error_payload = normalize_ajax_payload(
        jqXHR?.responseJSON ?? jqXHR?.responseText,
        jqXHR?.responseText || ""
    );

    reject(new Error(format_api_errors(error_payload)));
}

function build_ajax_options(url, options, resolve, reject) {
    return ({
        url,
        method: options.method || "GET",
        headers: options.headers || {},
        contentType: options.contentType || options.headers?.["Content-Type"] || undefined,
        data: options.body,
        processData: options.processData ?? false,
        success(response_data, _text_status, jqXHR) {
            handle_ajax_success(resolve, response_data, jqXHR);
        },
        error(jqXHR) {
            handle_ajax_error(reject, jqXHR);
        }
    });
}

export function ajax_json_request(url, options = {}) {
    return new Promise((resolve, reject) => {
        const jquery_instance = window.jQuery || window.$;

        if (!jquery_instance?.ajax) {
            reject(new Error("jQuery AJAX is not available."));
            return;
        }

        jquery_instance.ajax(build_ajax_options(url, options, resolve, reject));
    });
}
